import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/config';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PulsantiCondivisionePDF from './PulsantiCondivisionePDF';
import '../styles/Liste.css';
import { formattaData } from '../utils/dateUtils';
import { ProgrammaPDF } from './ProgrammiLavoro';

// Stili per il PDF del report giornaliero
const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica'
  },
  header: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: 'center',
    color: '#2c3e50',
    fontWeight: 'bold'
  },
  subheader: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: 'center',
    color: '#7f8c8d'
  },
  table: {
    display: 'table',
    width: '100%',
    marginTop: 10
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
    minHeight: 30,
    alignItems: 'center'
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    fontWeight: 'bold',
    fontSize: 9
  },
  tableCol: {
    padding: 5,
    fontSize: 8
  },
  col1: { width: '15%' },
  col2: { width: '8%' },
  col3: { width: '8%' },
  col4: { width: '8%' },
  col5: { width: '13%' },
  col6: { width: '15%' },
  col7: { width: '15%' },
  col8: { width: '18%' },
  footer: {
  marginTop: 20,
  textAlign: 'center',
  fontSize: 8,
  color: '#95a5a6'
}

});

// Componente PDF Report Giornaliero
function ReportGiornalieroPDF({ data, programmi, dipendentiMap, veicoliMap, cantieriMap, clientiMap }) {
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <Text style={pdfStyles.header}>REPORT GIORNALIERO</Text>
        <Text style={pdfStyles.subheader}>Data: {formattaData(data)}</Text>
        
        <View style={pdfStyles.table}>
          {/* Header tabella */}
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <Text style={[pdfStyles.tableCol, pdfStyles.col1]}>Dipendente</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col2]}>Inizio</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col3]}>Fine</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col4]}>Durata</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col5]}>Veicolo</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col6]}>Cliente</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col7]}>Cantiere</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col8]}>Note</Text>
          </View>
          
          {/* Righe dati */}
          {programmi.map((prog, index) => {
            const dipendente = dipendentiMap[prog.dipendenteId];
            const veicolo = veicoliMap[prog.veicoloId];
            const cantiere = cantieriMap[prog.cantiereId];
            const cliente = cantiere ? clientiMap[cantiere.clienteId] : null;
            
            return (
                <View key={index} style={pdfStyles.tableRow} wrap={false}>
                <Text style={[pdfStyles.tableCol, pdfStyles.col1]}>
                  {dipendente ? `${dipendente.cognome} ${dipendente.nome}` : 'N/D'}
                </Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col2]}>{prog.orarioInizio}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col3]}>{prog.orarioFine}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col4]}>{prog.durataLavoro}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col5]}>
                  {veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}
                </Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col6]}>
                  {cliente ? cliente.ragioneSociale : 'N/D'}
                </Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col7]}>
                  {cantiere ? cantiere.luogo : 'N/D'}
                </Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col8]}>
                  {prog.note || '-'}
                </Text>
              </View>
            );
          })}
        </View>

        <Text style={pdfStyles.footer}>
          Report generato il {new Date().toLocaleDateString('it-IT')} alle {new Date().toLocaleTimeString('it-IT')} - App Cantieri
        </Text>
      </Page>
    </Document>
  );
}

function ReportGiornaliero() {
  const [programmi, setProgrammi] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [veicoli, setVeicoli] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [clienti, setClienti] = useState([]);
  
  const [dataSelezionata, setDataSelezionata] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [reportGenerato, setReportGenerato] = useState(false);

  useEffect(() => {
    caricaTuttiDati();
  }, []);

  async function caricaTuttiDati() {
    await Promise.all([
      caricaDipendenti(),
      caricaVeicoli(),
      caricaCantieri(),
      caricaClienti(),
      caricaProgrammi()
    ]);
  }

  async function caricaDipendenti() {
    const dipendentiRef = ref(database, 'dipendenti');
    const snapshot = await get(dipendentiRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      setDipendenti(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaVeicoli() {
    const veicoliRef = ref(database, 'veicoli');
    const snapshot = await get(veicoliRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      setVeicoli(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaCantieri() {
    const cantieriRef = ref(database, 'cantieri');
    const snapshot = await get(cantieriRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      setCantieri(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaClienti() {
    const clientiRef = ref(database, 'clienti');
    const snapshot = await get(clientiRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      setClienti(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaProgrammi() {
    const programmiRef = ref(database, 'programmi-lavorativi');
    const snapshot = await get(programmiRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      const programmiArray = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      setProgrammi(programmiArray);
    } else {
      setProgrammi([]);
    }
  }

  function generaReport() {
    if (!dataSelezionata) {
      alert('Seleziona una data');
      return;
    }

    // Filtra programmi per la data selezionata
    const programmiFiltrati = programmi.filter(p => p.data === dataSelezionata);

    // Ordina per cognome dipendente
    programmiFiltrati.sort((a, b) => {
      const dipA = dipendenti.find(d => d.id === a.dipendenteId);
      const dipB = dipendenti.find(d => d.id === b.dipendenteId);
      
      if (!dipA || !dipB) return 0;
      
      return dipA.cognome.localeCompare(dipB.cognome);
    });

    setRisultati(programmiFiltrati);
    setReportGenerato(true);
  }

  function resetReport() {
    setDataSelezionata('');
    setRisultati([]);
    setReportGenerato(false);
  }

  function getDipendente(id) {
    return dipendenti.find(d => d.id === id);
  }

  function getVeicolo(id) {
    return veicoli.find(v => v.id === id);
  }

  function getCantiere(id) {
    return cantieri.find(c => c.id === id);
  }

  function getCliente(cantiereId) {
    const cantiere = getCantiere(cantiereId);
    return cantiere ? clienti.find(c => c.id === cantiere.clienteId) : null;
  }

  // Crea mappe per il PDF
  const dipendentiMap = {};
  dipendenti.forEach(d => { dipendentiMap[d.id] = d; });
  
  const veicoliMap = {};
  veicoli.forEach(v => { veicoliMap[v.id] = v; });
  
  const cantieriMap = {};
  cantieri.forEach(c => { cantieriMap[c.id] = c; });
  
  const clientiMap = {};
  clienti.forEach(c => { clientiMap[c.id] = c; });

  return (
    <div className="lista-container">
      <div className="lista-header">
        <h2>📅 Report Giornaliero</h2>
      </div>

      {/* Selettore data */}
      <div style={{background: '#f8f9fa', padding: '25px', borderRadius: '10px', marginBottom: '30px'}}>
        <div style={{display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap'}}>
          <div className="form-group" style={{marginBottom: 0, flex: 1, minWidth: '200px'}}>
            <label>Seleziona Data</label>
            <input
              type="date"
              value={dataSelezionata}
              onChange={(e) => setDataSelezionata(e.target.value)}
              style={{width: '100%'}}
            />
          </div>
          
          <button 
            onClick={generaReport} 
            className="btn-add"
            style={{padding: '12px 30px'}}
          >
            📊 Genera Report
          </button>
          
          {reportGenerato && (
            <button 
              onClick={resetReport} 
              className="btn-cancel"
              style={{padding: '12px 30px'}}
            >
              🔄 Reset
            </button>
          )}
        </div>
      </div>

      {/* Risultati */}
      {!reportGenerato ? (
        <div className="empty-state">
          <p>📅 Seleziona una data e clicca su "Genera Report"</p>
          <p>Vedrai tutti i dipendenti impegnati in quel giorno, ordinati per cognome.</p>
        </div>
      ) : risultati.length === 0 ? (
        <div className="empty-state">
          <p>❌ Nessun programma trovato per la data {formattaData(dataSelezionata)}</p>
          <p>Prova con un'altra data.</p>
        </div>
      ) : (
        <>
          {/* Header con info e bottone PDF */}
          <div style={{
            background: '#efd400',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div>
              <strong style={{fontSize: '18px'}}>📅 Report del {formattaData(dataSelezionata)}</strong>
              <p style={{margin: '5px 0 0 0', color: '#333'}}>
                {risultati.length} {risultati.length === 1 ? 'programma trovato' : 'programmi trovati'}
              </p>
            </div>
            
            <PulsantiCondivisionePDF
              documentoPDF={
                <ReportGiornalieroPDF 
                  data={dataSelezionata}
                  programmi={risultati}
                  dipendentiMap={dipendentiMap}
                  veicoliMap={veicoliMap}
                  cantieriMap={cantieriMap}
                  clientiMap={clientiMap}
                />
              }
              nomeFile={`report_giornaliero_${dataSelezionata}.pdf`}
              testoCondivisione={`📊 Report Giornaliero ${formattaData(dataSelezionata)}\n\n${risultati.length} ${risultati.length === 1 ? 'programma lavorativo' : 'programmi lavorativi'} in giornata`}
              emailDestinatario=""
              oggettoEmail={`Report Giornaliero - ${formattaData(dataSelezionata)}`}
              corpoEmail={`Buongiorno,\n\nallego il report giornaliero del ${formattaData(dataSelezionata)}.\n\nRiepilogo:\n- ${risultati.length} ${risultati.length === 1 ? 'programma lavorativo' : 'programmi lavorativi'}\n- ${new Set(risultati.map(p => p.dipendenteId)).size} ${new Set(risultati.map(p => p.dipendenteId)).size === 1 ? 'dipendente impegnato' : 'dipendenti impegnati'}\n\nCordiali saluti`}
            />
          </div>

          {/* 👇 TABELLA DESKTOP (nascosta su mobile) */}
          <div style={{overflowX: 'auto'}} className="desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dipendente</th>
                  <th>Orario Inizio</th>
                  <th>Orario Fine</th>
                  <th>Durata</th>
                  <th>Veicolo</th>
                  <th>Cliente</th>
                  <th>Cantiere</th>
                  <th>Note</th>
		  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {risultati.map(prog => {
                  const dipendente = getDipendente(prog.dipendenteId);
                  const veicolo = getVeicolo(prog.veicoloId);
                  const cantiere = getCantiere(prog.cantiereId);
                  const cliente = getCliente(prog.cantiereId);

                  return (
                    <tr key={prog.id}>
                      <td>
                        <strong>
                          {dipendente ? `${dipendente.cognome} ${dipendente.nome}` : 'N/D'}
                        </strong>
                      </td>
                      <td>{prog.orarioInizio}</td>
                      <td>{prog.orarioFine}</td>
                      <td><strong>{prog.durataLavoro}</strong></td>
                      <td>
                        {veicolo ? (
                          <>
                            {veicolo.marca} {veicolo.modello}
                            <br/>
                            <small style={{color: '#666'}}>({veicolo.targa})</small>
                          </>
                        ) : 'N/D'}
                      </td>
                      <td>{cliente ? cliente.ragioneSociale : 'N/D'}</td>
                      <td>{cantiere ? cantiere.luogo : 'N/D'}</td>
                      <td style={{maxWidth: '200px', fontSize: '12px'}}>
                        {prog.note || '-'}
                      </td>
<td className="actions">
  {dipendente && veicolo && cantiere && cliente && (
    <PulsantiCondivisionePDF
      documentoPDF={
        <ProgrammaPDF
          programma={prog}
          dipendente={dipendente}
          veicolo={veicolo}
          cantiere={cantiere}
          cliente={cliente}
        />
      }
      nomeFile={`programma-${prog.data}-${dipendente.cognome}.pdf`}
      testoCondivisione={`Programma Lavorativo ${formattaData(prog.data)} - ${dipendente.nome} ${dipendente.cognome} - ${cantiere.luogo}`}
      emailDestinatario=""
      oggettoEmail={`Programma Lavorativo - ${prog.data} - ${dipendente.cognome}`}
      corpoEmail={`Programma del ${formattaData(prog.data)}.\n${dipendente.nome} ${dipendente.cognome}\n${cantiere.luogo} - ${cliente.ragioneSociale}\n${prog.orarioInizio} - ${prog.orarioFine}\n${prog.durataLavoro}\n${prog.note || 'Nessuna nota'}`}
    />
  )}
</td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 👇 LAYOUT CARD MOBILE (nascosto su desktop) */}
          <div className="mobile-cards">
            {risultati.map(prog => {
              const dipendente = getDipendente(prog.dipendenteId);
              const veicolo = getVeicolo(prog.veicoloId);
              const cantiere = getCantiere(prog.cantiereId);
              const cliente = getCliente(prog.cantiereId);

              return (
                <div key={prog.id} className="mobile-card">
                  <div className="mobile-card-header">
                    👤 {dipendente ? `${dipendente.cognome} ${dipendente.nome}` : 'N/D'}
                  </div>
                  
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🕐 Orario:</span>
                    <span className="mobile-card-value">
                      {prog.orarioInizio} - {prog.orarioFine}
                    </span>
                  </div>

                  <div className="mobile-card-row">
                    <span className="mobile-card-label">⏱️ Durata:</span>
                    <span className="mobile-card-value">
                      <strong>{prog.durataLavoro}</strong>
                    </span>
                  </div>

                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🚗 Veicolo:</span>
                    <span className="mobile-card-value">
                      {veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}
                    </span>
                  </div>

                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🏢 Cliente:</span>
                    <span className="mobile-card-value">
                      {cliente ? cliente.ragioneSociale : 'N/D'}
                    </span>
                  </div>

                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🏗️ Cantiere:</span>
                    <span className="mobile-card-value">
                      {cantiere ? cantiere.luogo : 'N/D'}
                    </span>
                  </div>

                  {prog.note && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">📝 Note:</span>
                      <span className="mobile-card-value" style={{fontSize: '12px'}}>
                        {prog.note}
                      </span>
                    </div>
                  )}

{dipendente && veicolo && cantiere && cliente && (
  <div className="mobile-card-actions">
    <PulsantiCondivisionePDF
      documentoPDF={
        <ProgrammaPDF
          programma={prog}
          dipendente={dipendente}
          veicolo={veicolo}
          cantiere={cantiere}
          cliente={cliente}
        />
      }
      nomeFile={`programma-${prog.data}-${dipendente.cognome}.pdf`}
      testoCondivisione={`Programma Lavorativo ${formattaData(prog.data)} - ${dipendente.nome} ${dipendente.cognome}`}
      emailDestinatario=""
      oggettoEmail={`Programma Lavorativo - ${prog.data} - ${dipendente.cognome}`}
      corpoEmail={`Programma del ${formattaData(prog.data)}.\n${dipendente.nome} ${dipendente.cognome}\n${cantiere.luogo}\n${prog.orarioInizio} - ${prog.orarioFine}`}
    />
  </div>
)}

                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default ReportGiornaliero;
